import { MouseEvent, useState } from "react";

import AddLinkOutlinedIcon from "@mui/icons-material/AddLinkOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import {
	Box,
	Button,
	Divider,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Tooltip,
	Typography,
} from "@mui/material";

import { Translate } from "@/components/Application/Translate";
import { NewFolder } from "@/components/Application/WrapperHeadingLeft";
import { NewProcedureModal } from "@/components/Procedures/NewProcedure";

import { StyledMenu } from "@/helpers/components/StyledMenu";

interface ProceduresActionsProps {
	handleToggleAll: () => void;
}

export default function ProceduresActions({
	handleToggleAll,
}: ProceduresActionsProps) {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [procedureModal, toggleNewProcedureModal] = useState(false);
	const [procedureFolderModal, toggleNewProcedureFolderModal] = useState(false);
	const [newLinkModal, toggleNewLinkModal] = useState(false);

	const open = Boolean(anchorEl);

	const handleClick = (event: MouseEvent<HTMLElement>) =>
		setAnchorEl(event.currentTarget);
	const handleClose = () => setAnchorEl(null);

	const openFolder = () => {
		toggleNewProcedureFolderModal(true);
		handleClose();
	};
	const openLink = () => {
		toggleNewLinkModal(true);
		handleClose();
	};
	const openProcedure = () => {
		toggleNewProcedureModal(true);
		handleClose();
	};

	return (
		<>
			<Tooltip
				title={
					<Translate String="Procedures.Actions.Tooltip">
						Create a new folder, link, or procedure
					</Translate>
				}
				enterDelay={600}
			>
				<Box display="inline-flex">
					<Button
						sx={{
							borderRadius: 2,
							textTransform: "none",
							fontWeight: 800,
						}}
						id="procedures-actions-button"
						aria-controls={open ? "procedures-actions-menu" : undefined}
						aria-haspopup="menu"
						aria-expanded={open ? "true" : undefined}
						variant="contained"
						color="info"
						disableElevation
						onClick={handleClick}
						endIcon={<KeyboardArrowDownIcon />}
					>
						<Translate String="Procedures.Actions">Actions</Translate>
					</Button>
				</Box>
			</Tooltip>

			<StyledMenu
				id="procedures-actions-menu"
				MenuListProps={{
					"aria-labelledby": "procedures-actions-button",
					dense: true,
				}}
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				keepMounted
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
			>
				<Box px={1.5} pt={1} pb={0.5}>
					<Typography
						variant="overline"
						color="text.secondary"
						sx={{ letterSpacing: 0.6 }}
					>
						<Translate String="Procedures.Actions.Create">Create</Translate>
					</Typography>
				</Box>

				<MenuItem onClick={openFolder}>
					<ListItemIcon>
						<CreateNewFolderIcon color="primary" fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							<Translate
								HTMLTag="span"
								String="Procedures.NewProcedure.NewFolder"
							>
								New folder
							</Translate>
						}
						secondary={
							<Typography variant="caption" color="text.secondary">
								<Translate String="Procedures.NewProcedure.NewFolder.Desc">
									Organize procedures into folders
								</Translate>
							</Typography>
						}
					/>
				</MenuItem>

				<MenuItem onClick={openLink}>
					<ListItemIcon>
						<AddLinkOutlinedIcon color="info" fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							<Translate
								HTMLTag="span"
								String="Procedures.NewProcedure.NewLink"
							>
								New link
							</Translate>
						}
						secondary={
							<Typography variant="caption" color="text.secondary">
								<Translate String="Procedures.NewProcedure.NewLink.Desc">
									Create a shortcut to an external resource
								</Translate>
							</Typography>
						}
					/>
				</MenuItem>

				<MenuItem onClick={openProcedure}>
					<ListItemIcon>
						<PlaylistAddIcon color="success" fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							<Translate
								HTMLTag="span"
								String="Procedures.NewProcedure.CreateNewProcedure"
							>
								New procedure
							</Translate>
						}
						secondary={
							<Typography variant="caption" color="text.secondary">
								<Translate String="Procedures.NewProcedure.CreateNewProcedure.Desc">
									Add a new procedure to this area
								</Translate>
							</Typography>
						}
					/>
				</MenuItem>
			</StyledMenu>

			{/* Modals */}
			<NewProcedureModal
				open={procedureModal}
				onClose={() => toggleNewProcedureModal(false)}
			/>
			<NewFolder
				open={procedureFolderModal}
				onClose={() => toggleNewProcedureFolderModal(false)}
				isLink={false}
			/>
			<NewFolder
				open={newLinkModal}
				onClose={() => toggleNewLinkModal(false)}
				isLink={true}
			/>
		</>
	);
}
